import { PRICING_RULE_REGISTRY } from '../utils/templateEngine';
import type { PricingRuleHook, PricingRuleResult, TemplateCustomerContext } from '../types/ticketTypeTemplate';
import type { RenewalScheduleItem } from '@techgitter/contracts';
import { getProductBySku } from '../hooks/useProductCatalog';
import { getHolidays } from '../hooks/usePublicHolidays';
import { getRenewalSchedule } from '../hooks/useRenewalSchedule';
import { resolveTimeWindow } from '../utils/timeWindowResolver';
import { findScheduleLineForSku, termForSku } from '../utils/licenseScheduleMatch';
import { resolveEolp2Billing } from '../utils/eolp2Billing';
import { resolveAssignLicenseBilling, countUsernameLines } from '../utils/assignLicenseBilling';

const m365License: PricingRuleHook = ({ additionalDetails }) => {
  const licenseType = additionalDetails.licenseType as string;
  const costs: Record<string, number> = { premium: 35, standard: 22, basic: 12 };
  const unitCost = costs[licenseType];
  if (unitCost === undefined) return undefined;
  return { unitCost, explanation: `${licenseType} license pricing applied` };
};

const googleLicense: PricingRuleHook = ({ additionalDetails }) => {
  const licenseType = additionalDetails.licenseType as string;
  const costs: Record<string, number> = { starter: 11, standard: 27.50, plus: 41.80 };
  const unitCost = costs[licenseType];
  if (unitCost === undefined) return undefined;
  return { unitCost, explanation: `Google Workspace ${licenseType} license pricing applied` };
};

const domainYears: PricingRuleHook = ({ additionalDetails, base }) => {
  const years = additionalDetails.years as number;
  if (years <= 1) return undefined;
  return {
    quantity: years,
    lineDetailAppend: `Multi-year registration: ${years} years`,
    explanation: `Adjusted quantity for ${years}-year registration`,
  };
};

const backupStorage: PricingRuleHook = ({ additionalDetails, base }) => {
  const storageSize = additionalDetails.storageSize as number;
  if (storageSize > 1000) {
    return {
      quantity: base.quantity + 1,
      lineDetailAppend: 'Additional time for enterprise storage configuration (>1TB)',
      explanation: 'Extra setup time for enterprise storage volume',
    };
  }
  if (storageSize > 500) {
    return {
      quantity: base.quantity + 0.5,
      lineDetailAppend: 'Additional time for large storage configuration (>500GB)',
      explanation: 'Extra setup time for large storage volume',
    };
  }
  return undefined;
};

// --- Support-labour pricing (config-driven via pricing profiles) ---

type SupportLabourSlot = 'remote' | 'onsite' | 'instore';

/**
 * Fallback when no pricing profile resolves (config not loaded, or the SKU
 * missing from the catalog): the template's own line stands, and templates name
 * the business SKU. For a residential customer that would bill the business
 * rate, so swap `BSupport.*` for the matching `RSupport.*` product at its
 * catalog price — or leave the line alone if the catalog has no such product.
 */
function residentialFallback(customer: TemplateCustomerContext, sku: string): PricingRuleResult | undefined {
  if (customer.supportProfile?.customerType !== 'Residential') return undefined;
  if (!sku.startsWith('BSupport.')) return undefined;
  const residentialSku = `RSupport.${sku.slice('BSupport.'.length)}`;
  const product = getProductBySku(residentialSku);
  if (!product) return undefined;
  return {
    lineItemId: product.sku,
    unitCost: product.unitCost,
    explanation: `${product.displayName ?? product.sku} — residential rate (no pricing profile)`,
  };
}

function makeSupportLabour(slot: SupportLabourSlot): PricingRuleHook {
  return ({ customer, now, lineItemDraft }) => {
    const rateSlot = customer.resolvedPricingProfile?.[slot];
    if (!rateSlot) return residentialFallback(customer, lineItemDraft?.lineItemId ?? '');

    const tw = resolveTimeWindow(now, getHolidays());

    const useAfterHours = tw.isAfterHours && rateSlot.afterHoursSku;
    const sku = useAfterHours ? rateSlot.afterHoursSku! : rateSlot.sku;
    const unitCost = useAfterHours ? rateSlot.afterHoursUnitCost! : rateSlot.unitCost;
    const quantity = rateSlot.defaultQuantity[tw.window] ?? 0.50;

    const product = getProductBySku(sku);
    const displayName = product?.displayName ?? sku;

    return {
      lineItemId: sku,
      unitCost,
      quantity,
      explanation: `${displayName} — ${tw.window} rate, ${quantity * 60}min minimum`,
    };
  };
}

const supportLabour = makeSupportLabour('remote');
const instoreSupportLabour = makeSupportLabour('instore');

// --- License proration (annual only — monthly subscriptions are not pro-rated) ---

function monthsBetween(from: Date, to: Date): number {
  return (to.getFullYear() - from.getFullYear()) * 12
    + (to.getMonth() - from.getMonth());
}

const licenseProration: PricingRuleHook = ({ customer, now, additionalDetails }) => {
  // The license-picker stores the selected SKU directly. License-change
  // templates use `toLicense` for the new license being added.
  const sku = (additionalDetails.licenseType || additionalDetails.workspaceEdition || additionalDetails.toLicense) as string;
  if (!sku) return undefined;

  const product = getProductBySku(sku);
  if (!product) return undefined;

  // Catalog rate for the SKU *as billed*: a monthly amount for `.Monthly`
  // SKUs, an annual amount for `.Annual` ones. Naming it "monthly" is what made
  // the annual paths below easy to misread.
  const term = termForSku(sku);
  const catalogRate = product.unitCost;
  const termWord = term === 'annual' ? 'annual' : 'monthly';

  // Prefer the schedule passed via customerContext (keyed by dataverseCustomerId
  // and already loaded by useRenewalSchedule); fall back to the module cache
  // keyed by repairShoprCustomerId for any caller that still uses that path.
  const scheduleItems: RenewalScheduleItem[] =
    (customer.renewalSchedule as RenewalScheduleItem[] | undefined)
    ?? getRenewalSchedule(customer.repairShoprCustomerId);

  if (scheduleItems.length === 0) {
    return {
      lineItemId: sku,
      unitCost: catalogRate,
      explanation: `${product.displayName} — full ${termWord} rate (no customer schedule)`,
    };
  }

  // The customer's own line for this SKU, else the soonest-renewing sibling in
  // the same family. Never just "the first M365 line" — a customer holding both
  // Business Basic and Business Premium would otherwise be priced against
  // whichever the API listed first.
  const matchingItem = findScheduleLineForSku(scheduleItems, sku);

  if (!matchingItem) {
    // Nothing on the same cadence to co-terminate with — an annual license here
    // means the customer has no annual subscription yet, so it bills a full year.
    return {
      lineItemId: sku,
      unitCost: catalogRate,
      explanation: `${product.displayName} — full ${termWord} rate (no matching ${termWord} schedule)`,
    };
  }

  const frequency = matchingItem.frequency.toLowerCase();
  const nextRun = new Date(matchingItem.nextRun);

  if (frequency === 'monthly') {
    // Monthly subscriptions are not pro-rated — keep catalog unit price and
    // omit the proration blurb.
    return {
      lineItemId: sku,
      explanation: `${product.displayName} — monthly subscription (no proration)`,
    };
  }

  if (frequency === 'annually') {
    const monthsRemaining = monthsBetween(now, nextRun);
    const annualUnitCost = term === 'annual' ? catalogRate : catalogRate * 12;

    if (monthsRemaining <= 0) {
      return {
        lineItemId: sku,
        unitCost: annualUnitCost,
        explanation: `${product.displayName} — full annual rate ($${annualUnitCost.toFixed(2)}/yr)`,
      };
    }

    // (Original cost / qty) * (months til renewal / 12). Per-unit math, so qty = 1.
    const prorated = Math.round((annualUnitCost * monthsRemaining / 12) * 100) / 100;

    // Subscription "due" month = the month of nextRun (e.g. nextRun Oct 1 → "due October")
    const dueMonth = nextRun.toLocaleString('en-AU', { month: 'long' });

    return {
      lineItemId: sku,
      unitCost: prorated,
      lineDetailAppend: `pro-rated to co-incide with existing annual subscription due ${dueMonth}`,
      explanation: `${product.displayName} — prorated ${monthsRemaining}/12 months ($${annualUnitCost.toFixed(2)}/yr)`,
    };
  }

  // Unknown frequency — fall back to the catalog rate for the SKU's own term.
  return {
    lineItemId: sku,
    unitCost: catalogRate,
    explanation: `${product.displayName} — full ${termWord} rate (frequency: ${matchingItem.frequency})`,
  };
};

// --- Exchange Online Plan 2 (Increase Email Storage) ---

// Drives the EOLP2 line on the `m365_increase_email_storage` ticket. Term
// (monthly/annual), SKU, and the buffered/pro-rated charge are all decided by
// the shared resolver so the save-time recurring-schedule mutation stays in
// lock-step with the charge shown on the ticket. When the customer has no M365
// subscription schedule the line is suppressed upstream (block warning), and
// this returns quantity 0 as a defensive no-charge.
const emailStorageBuffered: PricingRuleHook = ({ customer, now }) => {
  const scheduleItems: RenewalScheduleItem[] =
    (customer.renewalSchedule as RenewalScheduleItem[] | undefined)
    ?? getRenewalSchedule(customer.repairShoprCustomerId);

  const billing = resolveEolp2Billing(scheduleItems, now);

  if (billing.blocked || !billing.sku) {
    return { quantity: 0, explanation: billing.explanation };
  }

  return {
    lineItemId: billing.sku,
    unitCost: billing.ticketUnitCost,
    quantity: billing.ticketQuantity,
    lineDetailAppend: billing.lineDetailAppend,
    explanation: billing.explanation,
  };
};

// --- Assign new M365 license (bulk license assignment) ---

// Drives the license line on the `m365_assign_license` ticket. Term (monthly/
// annual), SKU, per-unit charge (full month / full year / pro-rated), and the
// recurring schedule mutation are all decided by the shared resolver so the
// save-time schedule sync stays in lock-step with the charge on the ticket.
// Quantity is the license count (derived from the usernames, overridable).
const assignLicense: PricingRuleHook = ({ customer, now, additionalDetails }) => {
  const sku = additionalDetails.licenseType as string;
  if (!sku) return undefined;

  const count =
    Number(additionalDetails.licenseCount) ||
    countUsernameLines(additionalDetails.usernames) ||
    1;

  const scheduleItems: RenewalScheduleItem[] =
    (customer.renewalSchedule as RenewalScheduleItem[] | undefined)
    ?? getRenewalSchedule(customer.repairShoprCustomerId);

  const billing = resolveAssignLicenseBilling(scheduleItems, now, sku, count);
  if (!billing) return undefined;

  // Line detail is owned by the template's lineDetailTemplate (product name +
  // user list - month); the proration is reflected in the unit cost and the
  // pricing explanation tooltip, so no lineDetailAppend here.
  return {
    lineItemId: billing.sku,
    unitCost: billing.ticketUnitCost,
    quantity: billing.ticketQuantity,
    explanation: billing.explanation,
  };
};

// --- Onsite visit pricing helpers ---

function parseTimeToMinutes(timeStr: string | number | undefined): number | null {
  if (!timeStr || typeof timeStr === 'number') return null;
  const parts = timeStr.split(':');
  if (parts.length !== 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

function minutesBetweenTimes(startStr: string | number | undefined, endStr: string | number | undefined): number | null {
  const start = parseTimeToMinutes(startStr);
  const end = parseTimeToMinutes(endStr);
  if (start === null || end === null) return null;
  return Math.max(0, end - start);
}

/** Round minutes to nearest 15-min block with a 3-minute blanket.
 *  If the remainder past the last 15-min boundary is <= 3, don't round up. */
function roundToQuarterHours(totalMinutes: number): number {
  const quarters = Math.floor(totalMinutes / 15);
  const remainder = totalMinutes % 15;
  const roundedQuarters = remainder > 3 ? quarters + 1 : quarters;
  return Math.max(roundedQuarters, 1) * 0.25;
}

function resolveOnsiteDesignation(customer: TemplateCustomerContext): 'B' | 'R' {
  const onsiteSlot = customer.resolvedPricingProfile?.['onsite'];
  if (onsiteSlot?.sku) {
    return onsiteSlot.sku.startsWith('R') ? 'R' : 'B';
  }
  // Residential pricing profiles don't include an `onsite` slot, so the
  // resolved profile is empty for those customers. Fall back to the
  // customer's base SKU prefix derived from customerType.
  return customer.supportProfile?.baseSkuPrefix ?? 'B';
}

function resolveOnsiteLabourRate(
  customer: TemplateCustomerContext,
  prefix: 'B' | 'R',
): { sku: string; unitCost?: number } {
  const onsiteSlot = customer.resolvedPricingProfile?.['onsite'];
  if (onsiteSlot?.sku) {
    return { sku: onsiteSlot.sku, unitCost: onsiteSlot.unitCost };
  }
  const sku = `${prefix}Support.Onsite`;
  return { sku, unitCost: getProductBySku(sku)?.unitCost };
}

// --- Onsite visit pricing rules ---

const HOME_BASE_LOCATION = 'Kirrawee';
const HOME_BASE_NAME = 'TechSolvers';

function formatTimeForDetail(value: string | number | undefined): string {
  if (typeof value !== 'string' || value.trim() === '') return '';
  return value;
}

const onsiteCalloutStandard: PricingRuleHook = ({ additionalDetails, customer }) => {
  if (additionalDetails.calloutType !== 'standard') {
    return { quantity: 0, explanation: 'Hidden — extended callout selected' };
  }

  const prefix = resolveOnsiteDesignation(customer);
  const sku = `${prefix}CallOut.Standard`;
  const product = getProductBySku(sku);
  const productName = product?.displayName ?? sku;

  return {
    lineItemId: sku,
    quantity: 1,
    lineDetailAppend: productName,
    explanation: `${prefix === 'R' ? 'Residential' : 'Business'} standard callout fee`,
  };
};

const onsiteCalloutExtended: PricingRuleHook = ({ additionalDetails, customer, lineItemDraft }) => {
  if (additionalDetails.calloutType !== 'extended') {
    return { quantity: 0, explanation: 'Hidden — standard callout selected' };
  }

  const prefix = resolveOnsiteDesignation(customer);
  const { unitCost: onsiteRate } = resolveOnsiteLabourRate(customer, prefix);

  const isOutward = lineItemDraft.templateKey === 'callout_extended_outward';

  const minutes = isOutward
    ? minutesBetweenTimes(additionalDetails.departTechSolversTime, additionalDetails.arrivalTime)
    : minutesBetweenTimes(additionalDetails.departureTime, additionalDetails.arriveTechSolversTime);

  const quantity = minutes !== null ? roundToQuarterHours(minutes) : 0.25;
  const direction = isOutward ? 'outward' : 'inward';

  const sku = `${prefix}CallOut.Extended`;
  const product = getProductBySku(sku);
  const productName = product?.displayName ?? sku;

  const location = (additionalDetails.location as string) || '';

  const lineDetailAppend = isOutward
    ? `${productName} (Outbound - Departed ${HOME_BASE_LOCATION} ${formatTimeForDetail(additionalDetails.departTechSolversTime)}, Arrived ${location} ${formatTimeForDetail(additionalDetails.arrivalTime)})`
    : `${productName} (Return - Departed ${location} ${formatTimeForDetail(additionalDetails.departureTime)}, Arrived ${HOME_BASE_NAME} ${formatTimeForDetail(additionalDetails.arriveTechSolversTime)})`;

  const result: PricingRuleResult = {
    lineItemId: sku,
    quantity,
    lineDetailAppend,
    explanation: `${prefix === 'R' ? 'Residential' : 'Business'} extended callout — ${direction} trip${minutes !== null ? `, ${minutes}min → ${quantity}h` : ''}`,
  };

  if (onsiteRate !== undefined) {
    result.unitCost = onsiteRate;
  }

  return result;
};

const onsiteLabour: PricingRuleHook = ({ additionalDetails, customer }) => {
  const prefix = resolveOnsiteDesignation(customer);
  const { sku, unitCost } = resolveOnsiteLabourRate(customer, prefix);

  const minutes = minutesBetweenTimes(additionalDetails.arrivalTime, additionalDetails.departureTime);
  const quantity = minutes !== null ? Math.max(1, roundToQuarterHours(minutes)) : 1;

  const product = getProductBySku(sku);
  const displayName = product?.displayName ?? sku;

  const result: PricingRuleResult = {
    lineItemId: sku,
    quantity,
    explanation: `${displayName}${minutes !== null ? ` — ${minutes}min → ${quantity}h` : ''}`,
  };

  if (unitCost !== undefined) {
    result.unitCost = unitCost;
  }

  return result;
};

PRICING_RULE_REGISTRY['m365License'] = m365License;
PRICING_RULE_REGISTRY['googleLicense'] = googleLicense;
PRICING_RULE_REGISTRY['domainYears'] = domainYears;
PRICING_RULE_REGISTRY['backupStorage'] = backupStorage;
PRICING_RULE_REGISTRY['supportLabour'] = supportLabour;
PRICING_RULE_REGISTRY['instoreSupportLabour'] = instoreSupportLabour;
PRICING_RULE_REGISTRY['licenseProration'] = licenseProration;
PRICING_RULE_REGISTRY['emailStorageBuffered'] = emailStorageBuffered;
PRICING_RULE_REGISTRY['assignLicense'] = assignLicense;
PRICING_RULE_REGISTRY['onsiteCalloutStandard'] = onsiteCalloutStandard;
PRICING_RULE_REGISTRY['onsiteCalloutExtended'] = onsiteCalloutExtended;
PRICING_RULE_REGISTRY['onsiteLabour'] = onsiteLabour;
